import { FunctionComponent, useMemo, useState } from 'react'
import DaycareSelector from '@/components/DaycareSelector/daycareSelector.tsx'
import { Daycare } from '@/types/Daycare.ts'
import {
    Add,
    AdsClick,
    Fullscreen,
    FullscreenExit,
    Groups,
    HourglassBottom,
    LockOpen,
    Print,
    PrivacyTip,
    Refresh
} from '@mui/icons-material'
import Tabs from '@/components/_custom/Tabs/tabs.tsx'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique.tsx'
import styles from './headerPlanningDesktop.module.scss'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon.tsx'
import { SelectOptionsDeprecated } from '@/types/Select.ts'
import { CalendarView } from '../../utils/calendarParameters.ts'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple.tsx'
import { Parent } from '@/types/Parent.ts'
import DeleteIcon from '@mui/icons-material/Delete'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import PlanningInfoDialog from '../../Dialog/planningInfoDialog/planningInfoDialog.tsx'
import SettingsIcon from '@mui/icons-material/Settings'
import { PlanningRights, PlanningTab, PlanningTabsState } from '@/hooks/usePlanningRights.ts'
import { changeTabAndResetFilters, parentHaveChildRequest } from '../headerHelper.ts'

interface HeaderPlanningWebDesktop {
    currentDaycare: Daycare
    planningRights: PlanningRights
    planningTabs: PlanningTabsState
    currentView: string
    setCurrentView: (view: string) => void
    parent?: Parent
    refetchData: (isGlobalPlanning?: boolean) => void
    handleAddSlot: () => void
    haveScheduledSlots: boolean
    setSelectFilters: (filters: string[]) => void
    selectFilters: string[]
    filterEvents: (filters: string[]) => void
    selectFiltersOptions: SelectOptionsDeprecated
    exportCalendar: () => void
    requestNumber: number
    handeSetFullScreen: () => void
    isFullScreen: boolean
    setRequestSlotDialogOpen: (open: boolean) => void
    setScheduledSlotsDialogOpen: (open: boolean) => void
}

const HeaderPlanningDesktop: FunctionComponent<HeaderPlanningWebDesktop> = ({
    currentDaycare,
    planningTabs,
    planningRights,
    currentView,
    setCurrentView,
    parent,
    refetchData,
    handleAddSlot,
    haveScheduledSlots,
    setSelectFilters,
    selectFilters,
    filterEvents,
    selectFiltersOptions,
    exportCalendar,
    requestNumber,
    handeSetFullScreen,
    isFullScreen,
    setRequestSlotDialogOpen,
    setScheduledSlotsDialogOpen
}: HeaderPlanningWebDesktop) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const [isPlanningInfoDialogOpen, setPlanningInfoDialogOpen] = useState<boolean>(false)

    const viewOptions: SelectOptionsDeprecated = [
        {
            options: [
                { value: CalendarView.Current, name: t('current') },
                { value: CalendarView.OneDay, name: t('day') },
                { value: CalendarView.OneWeek, name: t('week') },
                { value: CalendarView.OneMonth, name: t('months') }
            ]
        }
    ]

    const staffViewOptions: SelectOptionsDeprecated = [
        {
            options: [
                { value: CalendarView.Current, name: t('current') },
                { value: CalendarView.OneDay, name: t('day') }
            ]
        }
    ]

    function handleSettings(): void {
        if (planningTabs.isStaffTab) {
            navigate(`/daycare?daycare=${currentDaycare.id}#staffSettings`)
        } else {
            navigate(`/daycare?daycare=${currentDaycare.id}#merger`)
        }
    }

    const haveChildRequest: boolean = useMemo((): boolean => {
        return parentHaveChildRequest(currentDaycare.id, parent)
    }, [parent])

    return (
        <div className={styles.HeaderPosition}>
            <div className={styles.Container}>
                <div className={styles.Left}>
                    {planningTabs.isStaffTab ? (
                        planningRights.canReadStaffRights && (
                            <>
                                <div className={styles.ButtonContainer}>
                                    <SelectUnique
                                        selectOptions={staffViewOptions}
                                        selectValue={currentView}
                                        parent={planningRights.isParent}
                                        setSelectValue={setCurrentView}
                                        label={t('calendarView')}
                                        className={styles.Selector}
                                    />
                                    <ButtonTextIcon
                                        type={'custom'}
                                        customText={t('staffList')}
                                        customIcon={<Groups />}
                                        className={styles.Button}
                                        onClick={() => navigate('/staff')}
                                    />
                                </div>
                            </>
                        )
                    ) : (
                        <>
                            <SelectUnique
                                selectOptions={viewOptions}
                                selectValue={currentView}
                                parent={planningRights.isParent}
                                setSelectValue={setCurrentView}
                                label={t('calendarView')}
                                className={styles.Selector}
                            />
                            {planningRights.isParent && (
                                <ButtonIcon
                                    icon={<Refresh />}
                                    onClick={() => refetchData(planningTabs.isGlobalTab)}
                                    className={styles.Icon}
                                    parent={planningRights.isParent}
                                />
                            )}
                        </>
                    )}
                </div>
                {(planningRights.isStaff || planningRights.isTrustee || planningRights.isCald) &&
                    planningTabs.tabs.length > 1 && (
                        <div className={styles.Center}>
                            <Tabs
                                value={planningTabs.activeIndex}
                                tabList={planningTabs.tabs.map((t: PlanningTab): string => t.label)}
                                onChange={(event, newValue) =>
                                    changeTabAndResetFilters(
                                        planningTabs,
                                        selectFilters,
                                        setSelectFilters,
                                        event,
                                        newValue
                                    )
                                }
                            />
                        </div>
                    )}
                <div className={styles.Right}>
                    {selectFilters.length > 0 && (
                        <ButtonIcon
                            icon={<DeleteIcon />}
                            onClick={() => filterEvents([])}
                            className={styles.Icon}
                            parent={planningRights.isParent}
                        />
                    )}
                    <SelectMultiple
                        selectOptions={selectFiltersOptions}
                        selectValues={selectFilters}
                        setSelectValues={filterEvents}
                        oneLine
                        className={styles.Selector}
                        label={t('filter')}
                        isParent={planningRights.isParent}
                    />
                </div>
            </div>
            <div className={styles.Container}>
                <div className={styles.Left}>
                    {user && user.daycares.length > 1 && (
                        <div className={styles.DaycareSelector}>
                            <DaycareSelector removeBackground />
                        </div>
                    )}
                </div>
                <div className={styles.Center}>
                    {!planningRights.isParent && (
                        <div className={styles.IconContainer}>
                            {planningRights.canReadOccasionalSlot && (
                                <a
                                    className={styles.IndicatorClickable}
                                    href={`/request?tab=${planningTabs.activeTabId}`}
                                    onClick={(e) => {
                                        if (!(e.ctrlKey || e.metaKey)) {
                                            e.preventDefault()
                                            navigate(`/request?tab=${planningTabs.activeTabId}`)
                                        }
                                    }}
                                >
                                    <ButtonIcon
                                        icon={<HourglassBottom />}
                                        onClick={() => {}}
                                        className={styles.Icon}
                                        badge={requestNumber}
                                    />
                                </a>
                            )}
                            <ButtonIcon
                                icon={<Refresh />}
                                onClick={() => refetchData(planningTabs.isGlobalTab)}
                                className={styles.Icon}
                            />
                            {((planningTabs.isOccasionalTab && planningRights.canWriteOccasionalSlot) ||
                                (planningTabs.isRegularTab && planningRights.canWriteContracts) ||
                                (planningTabs.isStaffTab && planningRights.canWriteStaffWorks)) && (
                                <ButtonIcon icon={<Add />} onClick={handleAddSlot} className={styles.Icon} />
                            )}
                            {planningRights.canExportCalendar && (
                                <ButtonIcon icon={<Print />} onClick={exportCalendar} className={styles.Icon} />
                            )}
                            {planningRights.canReadParamsDaycare && (
                                <ButtonIcon icon={<SettingsIcon />} onClick={handleSettings} className={styles.Icon} />
                            )}
                            <ButtonIcon
                                icon={isFullScreen ? <FullscreenExit /> : <Fullscreen />}
                                onClick={handeSetFullScreen}
                                className={styles.Icon}
                            />
                            {planningRights.isCald && (
                                <ButtonIcon
                                    icon={<PrivacyTip />}
                                    onClick={() => setPlanningInfoDialogOpen(true)}
                                    className={styles.Icon}
                                />
                            )}
                        </div>
                    )}
                </div>
                <div className={styles.Right}>
                    {planningRights.isParent
                        ? haveChildRequest && (
                              <ButtonTextIcon
                                  type={'custom'}
                                  className={styles.Button}
                                  customIcon={<AdsClick />}
                                  customText={t('requestSlots')}
                                  parent={planningRights.isParent}
                                  onClick={() => setRequestSlotDialogOpen(true)}
                              />
                          )
                        : haveScheduledSlots &&
                          planningRights.canWriteOccasionalSlot && (
                              <ButtonTextIcon
                                  type={'custom'}
                                  className={styles.Button}
                                  customIcon={<LockOpen />}
                                  customText={t('liberatedSlots')}
                                  onClick={() => setScheduledSlotsDialogOpen(true)}
                              />
                          )}
                </div>
            </div>
            {isPlanningInfoDialogOpen && (
                <PlanningInfoDialog
                    open={isPlanningInfoDialogOpen}
                    handleClose={() => setPlanningInfoDialogOpen(false)}
                    daycare={currentDaycare}
                />
            )}
        </div>
    )
}

export default HeaderPlanningDesktop
